// encounterForm.js
// Builds the printable "Encounter / Chart Update Form" HTML for one or more
// patients. This is a parameterized port of handwritten-template.html — the
// markup/classes are kept identical so the printed/PDF output matches the
// approved design. Demographics + allergies + current meds are pre-filled
// (sourced from FHIR / the practice DB). Layout is a vertical split — allergies
// + current medications on the left, a plain (unruled) Visit Notes box filling
// the right — for the provider to handwrite during the encounter.

const esc = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

// The template's self-contained stylesheet. Lives inside an isolated iframe /
// PDF document, so the generic selectors (body, table, td …) never leak into
// the app. A couple of screen-context overrides are appended at the bottom so
// the on-screen preview and the rasterized PDF render on a clean white page
// (the @media print block already handles the actual print path).
export const FORM_STYLES = `
  :root{
    --ink:#111418; --hair:#c3c9d1; --rule:#b3bac2; --label:#5a626c;
    --bar:#eef1f4; --accent:#10325a; --data:#13171c;
  }
  *{box-sizing:border-box;}
  body{margin:0; background:#fff; font-family:"IBM Plex Sans", system-ui, sans-serif;
    color:var(--ink); -webkit-font-smoothing:antialiased;}

  .sheet{width:8.5in; height:11in; margin:0 auto; padding:0.3in;
    background:#fff; position:relative;
    display:flex; flex-direction:column; overflow:hidden;}
  .sheet + .sheet{ page-break-before:always; break-before:page; }

  /* appointment-types strip (full width, very top) — connects to the patient strip below */
  .appt-types{display:flex; align-items:baseline; gap:8px; border:1px solid var(--ink);
    border-bottom:none; background:var(--bar); padding:3px 9px;}
  .appt-types .atlabel{font-size:10.5px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--label); white-space:nowrap;}
  .appt-types .atvals{font-size:14px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:var(--data);}

  /* demographics strip (full width) */
  .patient{display:grid; grid-template-columns:2.05fr 1.3fr .5fr .95fr 1.3fr 1.05fr .9fr;
    border:1px solid var(--ink);}
  .patient .pcell{padding:2px 7px; border-right:1px solid var(--hair); position:relative;}
  .patient .pcell:last-child{border-right:none;}
  .plabel{font-size:11.2px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:var(--label);}
  .pval{font-size:15.4px; font-weight:600; color:var(--data); line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
  .emr-flag{position:absolute; top:2px; right:5px; font-family:"IBM Plex Mono",monospace;
    font-size:10.9px; letter-spacing:.1em; color:#b7bdc6;}

  /* vertical split: clinical on the left (wider), notes fill the right.
     align-items:stretch keeps both columns — and the left column's border-right
     divider — the full height of the split. */
  .split{display:flex; gap:0; flex:1; min-height:0; margin-top:8px; align-items:stretch;}
  .col-left{flex:1.4; display:flex; flex-direction:column; min-width:0;
    padding-right:10px; border-right:1.5px solid var(--ink);}
  .col-right{flex:1; display:flex; flex-direction:column; min-height:0; padding-left:10px;}

  .section{margin-bottom:9px;}
  .sec-head{display:flex; align-items:baseline; gap:8px; background:var(--bar);
    border-left:3px solid var(--accent); padding:2px 8px;}
  .sec-head h2{font-size:17.3px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; margin:0;}
  .sec-head .count{margin-left:auto; font-family:"IBM Plex Mono",monospace; font-size:12.7px;
    letter-spacing:.08em; color:var(--label);}

  table{width:100%; border-collapse:collapse; margin-top:3px; table-layout:fixed;}
  th{font-size:12.7px; font-weight:600; letter-spacing:.05em; text-transform:uppercase; color:var(--label);
    text-align:left; padding:2px 5px; border-bottom:1.2px solid var(--ink);}
  td{border-bottom:1px solid var(--hair); border-right:1px solid var(--hair);
    height:26px; padding:0 5px; vertical-align:middle;}
  td:last-child{border-right:none;}
  td.idx{text-align:center; color:var(--label); font-family:"IBM Plex Mono", monospace; font-size:13.65px; padding:0;}
  /* wrap on screen + in the rasterized PDF (html2canvas doesn't clip td overflow,
     so nowrap text would overflow); print restores nowrap+ellipsis below. */
  td.dataval{color:var(--data); font-size:14.6px; font-weight:500; overflow:hidden; white-space:normal; overflow-wrap:anywhere; word-break:break-word;}
  /* Med table: table-layout:fixed hard-constrains the table to the column width
     (no overflow). The colgroup in medTable() sets a wide Date column for the
     stacked Fill/Rx lines, a narrow Dose, and an auto Medication col that takes
     the rest + ellipsizes. SIG is uppercased. */
  table.meds{table-layout:fixed;}
  td.medname{overflow:hidden; color:var(--data); font-size:14.6px; font-weight:500;}
  td.medname .mname{display:block; max-width:100%; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
  td.dose{color:var(--data); font-size:11.9px; font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
  td.datecell{white-space:nowrap; overflow:hidden; font-family:"IBM Plex Mono", monospace; font-size:11.2px; color:var(--data); line-height:1.3; padding:1px 5px;}
  td.datecell .dline{white-space:nowrap; font-weight:700;}
  td.datecell .dlabel{display:inline-block; width:44px; color:var(--label); font-weight:700;}
  td.sig{font-size:12.7px; font-style:italic; text-transform:uppercase; color:var(--label); padding:0 6px 2px 8px;
    height:auto; line-height:1.3; white-space:normal; overflow-wrap:anywhere; word-break:break-word; border-right:none;}
  tr.med-main > td:not(.idx){border-bottom:none;}
  tr.med-sig > td{border-bottom:1px solid var(--hair);}
  td.dataval .mname{font-weight:600;}
  td.empty{color:var(--label); font-size:14.6px; font-style:italic;}
  tr.blank td{height:26px;}

  /* notes fill the right column */
  .notes-section{flex:1; display:flex; flex-direction:column; min-height:0;}
  .notes{margin-top:3px; flex:1; padding:8px 11px; min-height:0;}

  /* RPM enrollment flag (top of right column) */
  .rpm-flag{display:flex; align-items:center; gap:6px; border:1px solid var(--ink); padding:3px 9px; margin-bottom:7px;}
  .rpm-box{display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px;
    border:1.5px solid var(--ink); font-size:15.4px; font-weight:700; line-height:1; flex:none;}
  .rpm-box.checked{background:#0a7d33; border-color:#0a7d33; color:#fff;}
  .rpm-label{font-size:15.4px; font-weight:700; letter-spacing:.1em;}
  .rpm-note{font-size:12.6px; color:var(--label);}
  .rpm-sub{margin-left:auto; font-family:"IBM Plex Mono",monospace; font-size:11.2px; color:var(--label); white-space:nowrap;}

  /* default pharmacy line (under RPM): NAME first (priority), location/phone as a
     subscript below — the right column is too narrow to keep them on one line. */
  .rx-flag{border:1px solid var(--ink); padding:3px 9px; margin-bottom:7px;}
  .rx-head{display:flex; align-items:baseline; gap:8px;}
  .rx-label{font-size:15.4px; font-weight:700; letter-spacing:.1em; white-space:nowrap; flex:none;}
  .rx-name{font-size:13px; font-weight:700; color:var(--data); line-height:1.2; min-width:0; overflow-wrap:anywhere;}
  .rx-sub{display:block; margin-top:1px; font-family:"IBM Plex Mono",monospace; font-size:11.2px; color:var(--label); line-height:1.3; white-space:normal; overflow-wrap:anywhere;}

  /* Vitals entry box (now at the TOP of the LEFT column) */
  .vitals-section{margin-bottom:9px;}
  .vitals-grid{display:grid; grid-template-columns:repeat(4,1fr); border:1px solid var(--ink);
    border-right:none; border-bottom:none; margin-top:3px;}
  .vitals-grid .vcell{border-right:1px solid var(--ink); border-bottom:1px solid var(--ink); height:42px; padding:2px 5px;}
  .vlabel{font-size:8.8px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:var(--label);}

  @page{ size:Letter; margin:0; }
  @media print{
    body{background:#fff;}
    /* width:auto is critical — a fixed 8.5in sheet gets clipped by the
       browser's default print margins. auto fits the printable area.
       display:block + overflow:visible (overriding the screen flex/clip) so the
       content flows from the top of page 1 and paginates naturally — a flex
       column with overflow:hidden is treated as atomic and was shoving the whole
       body onto page 2, leaving page 1 with just the header. */
    .sheet{ width:auto; height:auto; overflow:visible; display:block;
            margin:0; box-shadow:none; padding:0.3in; }
    .sheet + .sheet{ page-break-before:always; break-before:page; }
    /* Two columns via FLOATS + block (NOT flex) for print. A print flex container
       is monolithic — Chrome won't fragment it, so when the body is near the
       page-1 budget it bumps the WHOLE thing to page 2, leaving page 1 blank with
       just the header. Block+float fragments across pages, so the body starts on
       page 1, fills it, and breaks cleanly only when it truly overflows. The
       left column's min-height carries its border-right divider down the page. */
    .split{ display:block; }
    .split::after{ content:""; display:block; clear:both; }
    .col-left{ display:block; float:left; width:58%; padding-right:10px;
               border-right:1.5px solid var(--ink); min-height:8.2in; }
    .col-right{ display:block; float:right; width:41%; padding-left:10px; }
    .notes-section{ display:block; }
    .notes{ min-height:2.4in; }
    td.dataval{ white-space:nowrap; word-break:normal; text-overflow:ellipsis; }
    td.datecell{ white-space:nowrap; }
    .sec-head,.notes,.patient,.split,.col-left,.col-right,.rpm-flag,.rpm-box,.rx-flag,.vitals-grid,.appt-types,td,th{
      -webkit-print-color-adjust:exact; print-color-adjust:exact;}
  }
`;

const allergyRows = (allergies) => {
  const rows = (allergies || []).map(
    (a) =>
      `<tr>` +
      `<td class="dataval"><span class="mname">${esc(a.allergen)}</span></td>` +
      `<td class="dataval">${esc(a.reaction)}</td></tr>`
  );
  if (rows.length === 0) {
    rows.push(`<tr><td class="empty" colspan="2">No known drug allergies on file</td></tr>`);
  }
  // Always leave one blank row so staff can add an allergy by hand.
  rows.push(`<tr class="blank"><td></td><td></td></tr>`);
  return rows.join("");
};

// Surgical history (proprietary) -> rows of "Procedure | Date" ("—" when undated).
const surgeryRows = (surgeries) => {
  const rows = (surgeries || []).map(
    (s) =>
      `<tr>` +
      `<td class="dataval"><span class="mname">${esc(s.name)}</span></td>` +
      `<td class="dataval">${esc(s.date) || "—"}</td></tr>`
  );
  if (rows.length === 0) {
    rows.push(`<tr><td class="empty" colspan="2">No surgical history on file</td></tr>`);
  }
  rows.push(`<tr class="blank"><td></td><td></td></tr>`);
  return rows.join("");
};

// Each med spans two rows: row 1 = name / dose / date filled (the name gets the
// whole column since Sig is no longer competing for width), row 2 = the Sig
// underneath, full width and tiny so long instructions never clip a column.
// Date cell: stack Fill + Rx (prescribed) dates on two slim labeled lines.
const dateCell = (m) => {
  const fill = m.filled ? `<div class="dline"><span class="dlabel">Fill</span>${esc(m.filled)}</div>` : "";
  const rx = m.prescribed ? `<div class="dline"><span class="dlabel">Rx</span>${esc(m.prescribed)}</div>` : "";
  return `<td class="datecell">${fill}${rx}</td>`;
};

const medRow = (m) =>
  `<tr class="med-main">` +
  `<td class="medname"><span class="mname">${esc(m.name)}</span></td>` +
  `<td class="dose">${esc(m.dose)}</td>` +
  dateCell(m) + `</tr>` +
  `<tr class="med-sig"><td class="sig" colspan="3">${esc(m.sig)}</td></tr>`;

// Med table: explicit % columns so Dose + Date never get squeezed; the
// Medication name ellipsizes within its column.
const medTable = (rows) =>
  `<table class="meds">` +
  `<colgroup><col style="width:46%"><col style="width:18%"><col style="width:36%"></colgroup>` +
  `<thead><tr><th>Medication</th><th>Dose</th><th>Date</th></tr></thead>` +
  `<tbody>${rows}</tbody></table>`;

const medsSection = (medications) => {
  const meds = medications || [];
  if (meds.length === 0) {
    return medTable(`<tr><td class="empty" colspan="3">No active medications on file</td></tr>`);
  }
  return medTable(meds.map((m) => medRow(m)).join(""));
};

/**
 * Build the inner HTML for a single encounter form (one printed page).
 * @param {Object} d
 * @param {string} d.name      e.g. "DOE, JANE M."
 * @param {string} d.dob       e.g. "04 / 12 / 1968"
 * @param {string} d.sex       e.g. "F"
 * @param {string} d.mrn       e.g. "0048217"
 * @param {string} d.provider  e.g. "A. Rao, MD"
 * @param {string} d.dos       date of service, e.g. "06 / 05 / 2026"
 * @param {Array<{allergen:string, reaction:string}>} d.allergies
 * @param {Array<{name:string, dose:string, sig:string}>} d.medications
 */
export const buildSheet = (d) => {
  const meds = d.medications || [];
  return `
  <div class="sheet">
    ${(d.apptTypes && d.apptTypes.length) ? `<div class="appt-types"><span class="atlabel">Appt Types</span><span class="atvals">${esc(d.apptTypes.join("   ·   "))}</span></div>` : ""}
    <div class="patient">
      <div class="pcell"><span class="emr-flag">EMR</span><div class="plabel">Patient Name</div><div class="pval">${esc(
        d.name
      )}</div></div>
      <div class="pcell"><div class="plabel">DOB</div><div class="pval">${esc(d.dob)}${d.age ? ` (${esc(String(d.age))})` : ""}</div></div>
      <div class="pcell"><div class="plabel">Sex</div><div class="pval">${esc(d.sex)}</div></div>
      <div class="pcell"><div class="plabel">MRN</div><div class="pval">${esc(d.mrn)}</div></div>
      <div class="pcell"><div class="plabel">Provider</div><div class="pval">${esc(d.provider)}</div></div>
      <div class="pcell"><div class="plabel">Date of Service</div><div class="pval">${esc(d.dos)}</div></div>
      <div class="pcell"><div class="plabel">Appt Time</div><div class="pval">${esc(d.time)}</div></div>
    </div>

    <div class="split">
      <div class="col-left">
        <div class="vitals-section">
          <div class="sec-head"><h2>Vitals</h2></div>
          <div class="vitals-grid">
            <div class="vcell"><span class="vlabel">BP</span></div>
            <div class="vcell"><span class="vlabel">Pulse</span></div>
            <div class="vcell"><span class="vlabel">Temp</span></div>
            <div class="vcell"><span class="vlabel">Resp</span></div>
            <div class="vcell"><span class="vlabel">SpO₂</span></div>
            <div class="vcell"><span class="vlabel">Weight</span></div>
            <div class="vcell"><span class="vlabel">Height</span></div>
            <div class="vcell"><span class="vlabel">BMI</span></div>
          </div>
        </div>
        ${(d.surgeries && d.surgeries.length) ? `
        <div class="section">
          <div class="sec-head"><h2>Surgical History</h2></div>
          <table>
            <colgroup><col style="width:66%"><col style="width:34%"></colgroup>
            <thead><tr><th>Procedure</th><th>Date</th></tr></thead>
            <tbody>${surgeryRows(d.surgeries)}</tbody>
          </table>
        </div>` : ""}

        <div class="section">
          <div class="sec-head"><h2>Allergies</h2></div>
          <table>
            <colgroup><col style="width:46%"><col style="width:auto"></colgroup>
            <thead><tr><th>Allergen</th><th>Reaction</th></tr></thead>
            <tbody>${allergyRows(d.allergies)}</tbody>
          </table>
        </div>

        <div class="section">
          <div class="sec-head"><h2>Current Medications</h2><span class="count">${meds.length} active</span></div>
          ${medsSection(meds)}
        </div>
      </div>

      <div class="col-right">
        <div class="rpm-flag">
          <span class="rpm-box${d.rpm && d.rpm.enrolled ? " checked" : ""}">${d.rpm && d.rpm.enrolled ? "✓" : ""}</span>
          <span class="rpm-label">RPM</span>
          <span class="rpm-note">${d.rpm && d.rpm.enrolled ? "Enrolled" : "Not enrolled"}</span>
          ${d.rpm && d.rpm.enrolled && d.rpm.lastReading ? `<span class="rpm-sub">last reading ${esc(d.rpm.lastReading)}</span>` : ""}
        </div>
        ${d.pharmacy && d.pharmacy.name ? `<div class="rx-flag"><div class="rx-head"><span class="rx-label">PHARMACY</span><span class="rx-name">${esc(d.pharmacy.name)}</span></div>${d.pharmacy.detail ? `<span class="rx-sub">${esc(d.pharmacy.detail)}</span>` : ""}</div>` : ""}
        <div class="notes-section">
          <div class="sec-head"><h2>Visit Notes</h2></div>
          <div class="notes"></div>
        </div>
      </div>
    </div>
  </div>`;
};

/**
 * Wrap one or more sheets into a complete, self-contained HTML document
 * suitable for an isolated iframe used by the browser print dialog.
 */
export const buildDocument = (sheetsHtml, title = "Encounter Forms") => `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>${FORM_STYLES}</style>
</head>
<body>
${Array.isArray(sheetsHtml) ? sheetsHtml.join("\n") : sheetsHtml}
</body>
</html>`;

export default buildSheet;
